/**
 * Сервис для управления множественными ботами
 * Поддерживает бота по умолчанию и кастомных ботов команд
 */

import { api } from './api';

class BotManager {
  constructor() {
    this.defaultBot = {
      id: 'default',
      name: 'Sport Team',
      username: 'sport_team_bot',
      description: 'Универсальный бот для спортивных команд',
      isCustom: false,
      displayName: 'Sport Team',
      token: null
    };
    
    this.teamBots = new Map();
    this.currentTeamId = null;
  }

  async initialize() {
    try {
      await this.loadTeamBots();
      console.log('✅ BotManager инициализирован');
    } catch (error) {
      console.error('❌ Ошибка инициализации BotManager:', error);
    }
  }

  async loadTeamBots() {
    try {
      const bots = await api.request('/bots/teams');
      
      bots.forEach(bot => {
        // Преобразуем team_id в число для единообразия
        const teamId = parseInt(bot.team_id);
        
        this.teamBots.set(teamId, {
          ...bot,
          team_id: teamId,
          isCustom: true,
          displayName: bot.name || bot.username || 'Кастомный бот'
        });
      });
      
      console.log(`Загружено ${bots.length} кастомных ботов команд`);
    } catch (error) {
      console.error('Ошибка загрузки ботов команд:', error);
    }
  }

  async connectTeamBot(teamId, token) {
    try {
      const newBot = await api.request('/bots/teams/connect', {
        method: 'POST',
        body: JSON.stringify({ teamId, botToken: token })
      });
      
      this.teamBots.set(teamId, {
        ...newBot,
        team_id: teamId,
        isCustom: true,
        displayName: newBot.name || newBot.username || 'Кастомный бот'
      });
      
      console.log('✅ Бот подключен к команде', teamId, newBot);
      
      return newBot;
    } catch (error) {
      console.error('Ошибка подключения бота команды:', error);
      throw error;
    }
  }

  async updateTeamBotToken(teamId, token) {
    try {
      const updatedBot = await api.request(`/bots/teams/${teamId}/token`, {
        method: 'PUT',
        body: JSON.stringify({ botToken: token })
      });
      
      this.teamBots.set(teamId, {
        ...updatedBot,
        team_id: teamId,
        isCustom: true,
        displayName: updatedBot.name || updatedBot.username || 'Кастомный бот'
      });
      
      console.log('✅ Токен бота обновлен для команды', teamId);
      
      return updatedBot;
    } catch (error) {
      console.error('Ошибка обновления токена бота команды:', error);
      throw error;
    }
  }

  async disconnectTeamBot(teamId) {
    try {
      const response = await api.request(`/bots/teams/${teamId}`, {
        method: 'DELETE'
      });
      
      // Удаляем бота из локального кэша
      this.teamBots.delete(teamId);
      
      console.log('✅ Бот команды отключен:', teamId);
      return response;
    } catch (error) {
      console.error('Ошибка отключения бота команды:', error);
      throw error;
    }
  }

  async getTeamBotIcon(teamId) {
    try {
      const response = await api.request(`/bots/teams/${teamId}/icon`, {
        method: 'GET'
      });
      
      console.log('Получена иконка бота команды:', response);
      return response;
    } catch (error) {
      console.error('Ошибка получения иконки бота команды:', error);
      return null;
    }
  }

  getBotForTeam(teamId) {
    // Преобразуем teamId в число для единообразия
    const numericTeamId = parseInt(teamId);
    const customBot = this.teamBots.get(numericTeamId);
    
    if (customBot) {
      return customBot;
    }
    return this.defaultBot;
  }

  hasCustomBot(teamId) {
    // Преобразуем teamId в число для единообразия
    const numericTeamId = parseInt(teamId);
    return this.teamBots.has(numericTeamId);
  }

  setCurrentBot(teamId) {
    this.currentTeamId = teamId;
  }

  getCurrentBot() {
    if (this.currentTeamId) {
      return this.getBotForTeam(this.currentTeamId);
    }
    return this.defaultBot;
  }

  // Методы для получения информации о боте (без токена)
  getBotInfo(teamId) {
    const bot = this.getBotForTeam(teamId);
    const { token, ...botInfo } = bot;
    return botInfo;
  }

  // Метод для проверки валидности токена
  async validateToken(token) {
    try {
      const response = await api.request('/bots/validate-token', {
        method: 'POST',
        body: JSON.stringify({ token })
      });
      return response.valid;
    } catch (error) {
      console.error('Ошибка валидации токена:', error);
      return false;
    }
  }
}

export const botManager = new BotManager(); 